<template>
  <Drawer
    v-bind="getBindValue"
    :title="props.title"
    :open="props.open"
    :keyboard="keyboard"
    :root-class-name="bem.b()"
    @close="handleClose"
  >
    <template #title>
      <slot name="title" />
    </template>
    <template #extra>
      <slot name="extra" />
    </template>
    <div :class="[bem.e('container'), hasScroll ? bem.is('has-scroll') : '']">
      <div v-if="$slots.step" :class="bem.e('step')">
        <slot name="step" />
      </div>
      <div :class="[bem.e('content'), symbolClass]">
        <div v-if="hasScroll && scrollTop > 0" :class="bem.e('shadow')"></div>
        <div :class="[symbolComparedClass, bem.e('scroll')]">
          <slot />
        </div>
      </div>
      <div
        v-if="props.showFooter"
        :class="[bem.e('footer'), hasScroll ? bem.is('has-scroll') : '']"
      >
        <slot name="footer">
          <div :class="bem.e('footer-left')">
            <slot name="footer-left" />
          </div>
          <div :class="bem.e('footer-right')">
            <slot name="footer-right">
              <ll-button
                v-if="showCancel"
                :class="bem.m('cancel')"
                :label="props.cancelText"
                @click="handleCancel"
              >
              </ll-button>
              <ll-button
                type="primary"
                :loading="props.loading"
                :label="props.confirmText"
                @click="handleConfirm"
              >
              </ll-button>
            </slot>
          </div>
        </slot>
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, useAttrs } from 'vue'
import { Drawer } from 'ant-design-vue'
import { createNamespace } from '@ll-plus/utils'
import { omit } from 'lodash-es'
import { drawerProps, drawerEmits, drawerOptions } from './drawer'
import { useSecondConfirmation } from './components/second-confirmation'

defineOptions({ name: 'LlDrawer' })

const props = defineProps(drawerProps)

const emits = defineEmits(drawerEmits)

const attrs = useAttrs()

const bem = createNamespace('drawer')

const hasScroll = ref(false)

const scrollTop = ref(0)

const symbolClass = ref(`ll-darwer-${new Date().getTime()}`)

const symbolComparedClass = ref(`model-content-${new Date().getTime()}`)

const getBindValue = computed(() => ({
  ...drawerOptions,
  ...omit({ ...attrs }, [...Object.keys(drawerProps), 'rootClassName']),
}))

const handleClose = (): void => {
  if (props.useClose) {
    emits('close')
  } else {
    handleCancel()
    return
  }
  emits('update:open')
}
const handleCancel = (): void => {
  if (props.cancelConfirm) {
    useSecondConfirmation(
      props.cancelConfirmOptions || {
        title: '温馨提示',
        message: '你有未保存的更改，确定关闭？',
      }
    ).then(async () => {
      // 点击保存按钮则关闭弹窗和抽屉
      emits('cancel')
    })
    return
  }
  emits('cancel')
}
const handleConfirm = (): void => {
  emits('confirm')
}

const handleResize = async (value: boolean) => {
  await nextTick()
  const ele = document.getElementsByClassName(symbolClass.value)[0]
  const modalBody = ele.getElementsByClassName(symbolComparedClass.value)[0]
  const resizeObserver = new ResizeObserver((entries) => {
    // 处理大小变化的回调函数
    const { clientHeight } = entries[0].target
    const scrollHeight = ele.clientHeight
    hasScroll.value = clientHeight > scrollHeight
  })
  if (value) {
    resizeObserver.observe(modalBody)
    ele.addEventListener('scroll', () => {
      scrollTop.value = ele.scrollTop
    })
  } else {
    resizeObserver.unobserve(modalBody)
    ele.removeEventListener('scroll', () => {
      scrollTop.value = 0
    })
  }
}

const onScrollTop = async () => {
  await nextTick()
  if (!hasScroll.value) return
  const ele = document.getElementsByClassName(symbolClass.value)[0]
  ele.scrollTop = 0
}

watch(
  () => props.open,
  (val) => {
    handleResize(val)
  }
)

defineExpose({
  onScrollTop,
})
</script>
